/**
 * Medicare Supplement Plan Eligibility Utility
 * Based on state-specific rules and eligibility windows
 */

export interface EligibilityInput {
  state: string;
  userBirthday: Date;
  currentDate?: Date;
  hasExistingMedSupp?: boolean;
  isInAnotherWindowOfferingPartD?: boolean;
  medSuppAnniversaryDate?: Date;
  partBEnrollmentDate?: Date; // When user enrolled in Part B (for 6-month window calculation)
  hasQualifyingLifeEvent?: boolean; // For Special Enrollment Period (SEP) determination
  qualifyingLifeEventType?: string; // Type of qualifying event (e.g., 'moved', 'lost_coverage', etc.)
}

export interface EligibilityOutput {
  isApplicable: boolean;
  canWeSupport: boolean;
  eligibilityWindow: {
    startDate: Date | null;
    endDate: Date | null;
    description: string;
  };
  canSwitchCompanies: boolean;
  hasPreExistingConditionWaiting: boolean;
  requiresExistingMedSupp: boolean;
  switchRules: string;
  calculationType:
    | 'start_and_end_plan'
    | 'year_round'
    | 'booklet_algorithm'
    | 'not_applicable';
  reason?: string;
}

interface StateRule {
  eligibilityPeriod: string;
  canSwitchCompanies: boolean;
  hasPreExistingWaiting: boolean;
  switchRules: string;
  requiresExistingMedSupp: boolean;
  supportedInPartDWindow: boolean;
  calculateWindow: (
    birthday: Date,
    currentDate: Date,
    anniversaryDate?: Date
  ) => { start: Date | null; end: Date | null };
}

/**
 * Helper function to get the relevant birthday anniversary for window calculation
 * Returns this year's birthday for most cases, but may return last year's if
 * this year's birthday is far in the future
 * Uses UTC to avoid timezone issues
 */
function getCurrentYearBirthday(birthday: Date, currentDate: Date): Date {
  const currentYear = currentDate.getUTCFullYear();
  const thisYearsBirthday = new Date(
    Date.UTC(currentYear, birthday.getUTCMonth(), birthday.getUTCDate())
  );

  // Always use this year's birthday for window calculations
  // State-specific windows can start before the actual birthday
  return thisYearsBirthday;
}

function createBirthdayWindow(
  startOffsetDays: number,
  endOffsetDays: number
): StateRule['calculateWindow'] {
  return (birthday, currentDate) => {
    const yearBirthday = getCurrentYearBirthday(birthday, currentDate);
    const start = new Date(yearBirthday);
    const end = new Date(yearBirthday);
    start.setDate(start.getDate() + startOffsetDays);
    end.setDate(end.getDate() + endOffsetDays);
    return { start, end };
  };
}

const STATE_RULES: Record<string, StateRule> = {
  CT: {
    eligibilityPeriod: 'All year',
    canSwitchCompanies: true,
    hasPreExistingWaiting: true,
    switchRules: 'No restrictions',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: true,
    calculateWindow: () => ({ start: null, end: null }), // Year-round
  },
  NY: {
    eligibilityPeriod: 'All year',
    canSwitchCompanies: true,
    hasPreExistingWaiting: true,
    switchRules: 'No restrictions',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: true,
    calculateWindow: () => ({ start: null, end: null }), // Year-round
  },
  CA: {
    eligibilityPeriod: '60 days starting on birthday',
    canSwitchCompanies: true,
    hasPreExistingWaiting: false,
    switchRules: 'Switch to plan of equal or lesser benefit',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: false,
    calculateWindow: createBirthdayWindow(0, 60),
  },
  ID: {
    eligibilityPeriod: '63 days starting on birthday',
    canSwitchCompanies: true,
    hasPreExistingWaiting: false,
    switchRules: 'Switch to plan of equal or lesser benefit',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: false,
    calculateWindow: createBirthdayWindow(0, 63),
  },
  IL: {
    eligibilityPeriod:
      '63 days starting on Feb 1 for 65+, 45 days starting on birthday',
    canSwitchCompanies: false,
    hasPreExistingWaiting: false,
    switchRules: 'Stay with current provider',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: false,
    // This would need additional logic to determine if user is 65+
    calculateWindow: createBirthdayWindow(0, 63),
  },
  KY: {
    eligibilityPeriod: '60 days starting first day after birthday',
    canSwitchCompanies: true,
    hasPreExistingWaiting: false,
    switchRules: 'Stay with same plan type',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: false,
    calculateWindow: createBirthdayWindow(1, 61),
  },
  LA: {
    eligibilityPeriod: '30 days before to 63 days after birthday',
    canSwitchCompanies: false,
    hasPreExistingWaiting: false,
    switchRules: 'Switch to plan of equal or lesser benefit',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: false,
    calculateWindow: createBirthdayWindow(-30, 63),
  },
  MD: {
    eligibilityPeriod: '30 days starting on birthday',
    canSwitchCompanies: true,
    hasPreExistingWaiting: false,
    switchRules:
      'Must have Med Supp already AND must remain in plan or equal or lesser benefit',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: false,
    calculateWindow: createBirthdayWindow(0, 30),
  },
  NV: {
    eligibilityPeriod: '60 days from first day of birth month',
    canSwitchCompanies: true,
    hasPreExistingWaiting: false,
    switchRules: 'Switch to plan of equal or lesser benefit',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: false,
    calculateWindow: (birthday, currentDate) => {
      const yearBirthday = getCurrentYearBirthday(birthday, currentDate);
      const start = new Date(
        Date.UTC(yearBirthday.getUTCFullYear(), yearBirthday.getUTCMonth(), 1)
      );
      const end = new Date(
        Date.UTC(
          start.getUTCFullYear(),
          start.getUTCMonth(),
          start.getUTCDate() + 60
        )
      );
      return { start, end };
    },
  },
  OK: {
    eligibilityPeriod: '60 days, starting from birthday',
    canSwitchCompanies: true,
    hasPreExistingWaiting: false,
    switchRules: 'Switch to plan of equal or lesser benefit',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: false,
    calculateWindow: createBirthdayWindow(0, 60),
  },
  OR: {
    eligibilityPeriod: 'starts 30 days before birthday and ends 29 days after',
    canSwitchCompanies: true,
    hasPreExistingWaiting: false,
    switchRules: 'Switch to plan of equal or lesser benefit',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: false,
    calculateWindow: createBirthdayWindow(-30, 29),
  },
  UT: {
    eligibilityPeriod: '60 days starting on birthday',
    canSwitchCompanies: false,
    hasPreExistingWaiting: false,
    switchRules: 'Plan must be of equal or lesser plans',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: false,
    calculateWindow: createBirthdayWindow(0, 60),
  },
  VA: {
    eligibilityPeriod: '60 days starting on birthday',
    canSwitchCompanies: true,
    hasPreExistingWaiting: false,
    switchRules:
      'Plan must offer same standard coverages OR be named Med Supp X, Y, Z',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: false,
    calculateWindow: createBirthdayWindow(0, 60),
  },
  WY: {
    eligibilityPeriod: '63 days starting on birthday',
    canSwitchCompanies: true,
    hasPreExistingWaiting: false,
    switchRules: 'Plan must be of equal or lesser plans',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: false,
    calculateWindow: createBirthdayWindow(0, 63),
  },
  MO: {
    eligibilityPeriod: '30 days before anniversary of med supp to start 60+',
    canSwitchCompanies: true,
    hasPreExistingWaiting: false,
    switchRules: 'Switch to equal benefits',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: false,
    calculateWindow: (birthday, currentDate, anniversaryDate) => {
      if (!anniversaryDate) return { start: null, end: null };
      const start = new Date(anniversaryDate);
      start.setDate(start.getDate() - 30);
      const end = new Date(anniversaryDate);
      end.setDate(end.getDate() + 60);
      return { start, end };
    },
  },
  WA: {
    eligibilityPeriod: 'Year-round',
    canSwitchCompanies: true,
    hasPreExistingWaiting: false,
    switchRules: 'Switch to equal benefits',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: false,
    calculateWindow: () => ({ start: null, end: null }), // Year-round
  },
  ME: {
    eligibilityPeriod: 'Year-round',
    canSwitchCompanies: true,
    hasPreExistingWaiting: false,
    switchRules: 'Plan must be of equal or lesser plans',
    requiresExistingMedSupp: false,
    supportedInPartDWindow: false,
    calculateWindow: () => ({ start: null, end: null }), // Year-round
  },
};

/**
 * Determines if the current date falls within the eligibility window
 */
function isWithinWindow(
  currentDate: Date,
  start: Date | null,
  end: Date | null
): boolean {
  // Year-round eligibility
  if (start === null && end === null) {
    return true;
  }

  if (!start || !end) {
    return false;
  }

  return currentDate >= start && currentDate <= end;
}

/**
 * Main eligibility determination function
 * Follows the flowchart logic: Calculates? Outputs → Is it applicable? → Can we support it? → Algorithm type
 */
export function determineMedicareSupEligibility(
  input: EligibilityInput
): EligibilityOutput {
  const {
    state,
    userBirthday,
    currentDate = new Date(),
    hasExistingMedSupp = false,
    isInAnotherWindowOfferingPartD = false,
    medSuppAnniversaryDate,
  } = input;

  // Step 1: Check if state is supported
  const stateRule = STATE_RULES[state];

  if (!stateRule) {
    return {
      isApplicable: false,
      canWeSupport: false,
      eligibilityWindow: {
        startDate: null,
        endDate: null,
        description: 'State not supported',
      },
      canSwitchCompanies: false,
      hasPreExistingConditionWaiting: false,
      requiresExistingMedSupp: false,
      switchRules: 'N/A',
      calculationType: 'not_applicable',
      reason: `State "${state}" is not in the supported states list`,
    };
  }

  // Step 2: Calculate eligibility window
  const window = stateRule.calculateWindow(
    userBirthday,
    currentDate,
    medSuppAnniversaryDate
  );
  const isYearRound = window.start === null && window.end === null;

  // Step 3: Check if user meets Med Supp requirement
  const meetsRequirement =
    !stateRule.requiresExistingMedSupp || hasExistingMedSupp;

  // Step 4: Is it applicable? (checks state and Med Supp requirement)
  const isApplicable = meetsRequirement;

  if (!isApplicable) {
    return {
      isApplicable: false,
      canWeSupport: false,
      eligibilityWindow: {
        startDate: window.start,
        endDate: window.end,
        description: stateRule.eligibilityPeriod,
      },
      canSwitchCompanies: stateRule.canSwitchCompanies,
      hasPreExistingConditionWaiting: stateRule.hasPreExistingWaiting,
      requiresExistingMedSupp: stateRule.requiresExistingMedSupp,
      switchRules: stateRule.switchRules,
      calculationType: 'not_applicable',
      reason: 'User does not meet Med Supp requirement',
    };
  }

  // Step 5: Can we support it? (checks eligibility window)
  const isInWindow = isWithinWindow(currentDate, window.start, window.end);

  // Step 6: Check Part D window restriction
  const canSupport =
    isInWindow &&
    (!isInAnotherWindowOfferingPartD || stateRule.supportedInPartDWindow);

  if (
    !canSupport &&
    isInAnotherWindowOfferingPartD &&
    !stateRule.supportedInPartDWindow
  ) {
    return {
      isApplicable: true,
      canWeSupport: false,
      eligibilityWindow: {
        startDate: window.start,
        endDate: window.end,
        description: stateRule.eligibilityPeriod,
      },
      canSwitchCompanies: stateRule.canSwitchCompanies,
      hasPreExistingConditionWaiting: stateRule.hasPreExistingWaiting,
      requiresExistingMedSupp: stateRule.requiresExistingMedSupp,
      switchRules: stateRule.switchRules,
      calculationType: 'not_applicable',
      reason:
        'User is in another window offering Part D, which is not supported for this state',
    };
  }

  if (!canSupport && !isInWindow) {
    return {
      isApplicable: true,
      canWeSupport: false,
      eligibilityWindow: {
        startDate: window.start,
        endDate: window.end,
        description: stateRule.eligibilityPeriod,
      },
      canSwitchCompanies: stateRule.canSwitchCompanies,
      hasPreExistingConditionWaiting: stateRule.hasPreExistingWaiting,
      requiresExistingMedSupp: stateRule.requiresExistingMedSupp,
      switchRules: stateRule.switchRules,
      calculationType: 'not_applicable',
      reason: 'Current date is outside the eligibility window',
    };
  }

  // Step 7: Determine calculation type
  let calculationType:
    | 'start_and_end_plan'
    | 'year_round'
    | 'booklet_algorithm' = 'booklet_algorithm';

  if (isYearRound) {
    calculationType = 'year_round';
  } else if (window.start && window.end) {
    calculationType = 'start_and_end_plan';
  }

  return {
    isApplicable: true,
    canWeSupport: canSupport,
    eligibilityWindow: {
      startDate: window.start,
      endDate: window.end,
      description: stateRule.eligibilityPeriod,
    },
    canSwitchCompanies: stateRule.canSwitchCompanies,
    hasPreExistingConditionWaiting: stateRule.hasPreExistingWaiting,
    requiresExistingMedSupp: stateRule.requiresExistingMedSupp,
    switchRules: stateRule.switchRules,
    calculationType,
  };
}

/**
 * Eligibility case types for routing users through the Medicare enrollment funnel
 */
export type EligibilityCase =
  | 'inIEP' // Initial Enrollment Period
  | 'inIEPAEP' // In both IEP and Annual Enrollment Period
  | 'inSEP' // Special Enrollment Period
  | 'inAEP' // Annual Enrollment Period (Oct 15 - Dec 7)
  | 'inMAOEP' // Medicare Advantage Open Enrollment Period (Jan 1 - Mar 31)
  | 'inGEPSEP' // General Enrollment Period (Jan 1 - Mar 31) / Special Enrollment Period
  | 'in6MWMS' // In 6 Month Window for Med Supp
  | 'sendOut' // Not supported - send to external resource
  | 'inSWMSNYR' // In State Window for Med Supp - Not Year Round
  | 'inSWMSYR'; // In State Window for Med Supp - Year Round

/**
 * Converts the detailed EligibilityOutput to a simplified EligibilityCase for routing
 *
 * This function maps the Medicare Supplement eligibility determination to specific
 * enrollment period cases based on the flowchart logic.
 *
 * **Priority Order (all 10 cases can be returned):**
 *
 * 1. **inIEPAEP** - In both IEP and AEP (Oct 15 - Dec 7 during the IEP window)
 * 2. **inIEP** - Initial Enrollment Period (7 months around turning 65)
 * 3. **inSEP** - Special Enrollment Period (has qualifying life event, not in GEP dates)
 * 4. **inGEPSEP** - General Enrollment Period + SEP (has qualifying event during Jan-Mar)
 * 5. **in6MWMS** - 6-month Med Supp window after Part B enrollment (guaranteed issue)
 * 6. **inAEP** - Annual Enrollment Period (October 15 - December 7)
 * 7. **inMAOEP** - Medicare Advantage Open Enrollment Period (January 1 - March 31)
 * 8. **sendOut** - Not supported (state not supported or outside eligibility window)
 * 9. **inSWMSYR** - In State Window for Med Supp - Year Round (CT, NY, WA, ME)
 * 10. **inSWMSNYR** - In State Window for Med Supp - Not Year Round (all other supported states)
 *
 * @param eligibilityOutput - The detailed eligibility result from determineMedicareEligibility
 * @param input - The original eligibility input containing birthday and dates
 * @returns EligibilityCase - The specific case type for routing decisions
 */
export function convertToEligibilityCase(
  eligibilityOutput: EligibilityOutput,
  input: EligibilityInput
): EligibilityCase {
  const currentDate = input.currentDate || new Date();
  const userBirthday = input.userBirthday;

  // First, check if user is in Initial Enrollment Period (IEP)
  // IEP takes priority as it's the first-time Medicare enrollment window.
  // When that window also falls in AEP, route the overlap separately.
  if (isInInitialEnrollmentPeriod(userBirthday, currentDate)) {
    if (isInAnnualEnrollmentPeriod(currentDate)) {
      return 'inIEPAEP';
    }
    return 'inIEP';
  }

  // Check if user has a qualifying life event for Special Enrollment Period (SEP)
  // SEP allows enrollment outside of normal periods due to specific life events:
  // - Moving to a new service area
  // - Losing other health coverage
  // - Leaving employer coverage
  // - Returning to the US after living abroad
  // - Being released from incarceration
  // - Losing Medicaid coverage
  if (input.hasQualifyingLifeEvent) {
    // If also in GEP dates (Jan 1 - Mar 31), can be classified as combined GEP/SEP
    if (isInMedicareAdvantageOEP(currentDate)) {
      return 'inGEPSEP';
    }
    return 'inSEP';
  }

  // Check if user is in the 6-month Med Supp window after Part B enrollment
  // This is the guaranteed-issue period for Med Supp plans
  const sixMonthWindow = get6MonthMedSuppWindow(
    userBirthday,
    currentDate,
    input.partBEnrollmentDate
  );
  if (sixMonthWindow.isInWindow) {
    return 'in6MWMS';
  }

  // Check if in Annual Enrollment Period (AEP) - October 15 to December 7
  if (isInAnnualEnrollmentPeriod(currentDate)) {
    return 'inAEP';
  }

  // Check if in Medicare Advantage Open Enrollment Period (MA-OEP) - January 1 to March 31
  // Or General Enrollment Period (GEP) which is the same dates
  if (isInMedicareAdvantageOEP(currentDate)) {
    // MA-OEP is for people already in MA plans to switch
    // GEP is for people who missed IEP to enroll in Part A/B
    // For routing purposes, we can combine these or use MAOEP
    return 'inMAOEP';
  }

  // If not applicable or we can't support them for Med Supp state rules, send them out
  if (!eligibilityOutput.isApplicable || !eligibilityOutput.canWeSupport) {
    return 'sendOut';
  }

  // Determine if this is a year-round state (no specific start/end dates)
  const isYearRound = eligibilityOutput.calculationType === 'year_round';

  // Year-round states (CT, NY, WA, ME)
  if (isYearRound) {
    return 'inSWMSYR'; // In State Window for Med Supp - Year Round
  }

  // States with specific eligibility windows
  // (CA, ID, IL, KY, LA, MD, NV, OK, OR, UT, VA, WY, MO)
  return 'inSWMSNYR'; // In State Window for Med Supp - Not Year Round
}

/**
 * Helper function to check if user is in their Initial Enrollment Period (IEP)
 * IEP is the 7-month period around turning 65 (3 months before, month of, 3 months after)
 *
 * Note: This is separate from Med Supp state windows and applies to all Medicare enrollment
 */
export function isInInitialEnrollmentPeriod(
  birthday: Date,
  currentDate: Date = new Date()
): boolean {
  // Calculate 65th birthday using UTC components to avoid timezone issues
  const age65Date = new Date(
    Date.UTC(
      birthday.getUTCFullYear() + 65,
      birthday.getUTCMonth(),
      birthday.getUTCDate()
    )
  );

  // IEP starts 3 months before 65th birthday
  const iepStart = new Date(
    Date.UTC(age65Date.getUTCFullYear(), age65Date.getUTCMonth() - 3, 1)
  );

  // IEP ends on the last day of the month 3 months after turning 65
  const iepEnd = new Date(
    Date.UTC(age65Date.getUTCFullYear(), age65Date.getUTCMonth() + 4, 0)
  );

  return currentDate >= iepStart && currentDate <= iepEnd;
}

/**
 * Helper function to check if user is in the 6-month Med Supp guaranteed-issue window
 * This is the 6-month period after enrolling in Medicare Part B (typically at age 65)
 * During this time, you can buy any Med Supp policy sold in your state without medical underwriting
 *
 * @param birthday - User's date of birth
 * @param currentDate - The date to check against (defaults to now)
 * @param partBEnrollmentDate - Optional Part B enrollment date (defaults to 65th birthday month)
 * @returns Object with isInWindow boolean and window dates
 */
export function get6MonthMedSuppWindow(
  birthday: Date,
  currentDate: Date = new Date(),
  partBEnrollmentDate?: Date
): { isInWindow: boolean; startDate: Date; endDate: Date } {
  // If Part B enrollment date is provided, use it. Otherwise assume it's the 65th birthday month
  // Use UTC to avoid timezone issues
  const enrollmentDate = partBEnrollmentDate
    ? new Date(
        Date.UTC(
          partBEnrollmentDate.getUTCFullYear(),
          partBEnrollmentDate.getUTCMonth(),
          1
        )
      )
    : new Date(
        Date.UTC(birthday.getUTCFullYear() + 65, birthday.getUTCMonth(), 1)
      );

  // 6-month window starts on the first day of the month you turn 65 and have Part B
  const windowStart = new Date(
    Date.UTC(enrollmentDate.getUTCFullYear(), enrollmentDate.getUTCMonth(), 1)
  );

  // Window ends on the last day of the 6th month
  const windowEnd = new Date(
    Date.UTC(windowStart.getUTCFullYear(), windowStart.getUTCMonth() + 6, 0)
  );

  const isInWindow = currentDate >= windowStart && currentDate <= windowEnd;

  return {
    isInWindow,
    startDate: windowStart,
    endDate: windowEnd,
  };
}

/**
 * Helper function to check if current date is in Annual Enrollment Period (AEP)
 * AEP runs from October 15 to December 7 every year
 */
export function isInAnnualEnrollmentPeriod(
  currentDate: Date = new Date()
): boolean {
  // Use UTC components to avoid timezone issues with ISO date strings
  const month = currentDate.getUTCMonth(); // 0-indexed (0 = January, 9 = October, 11 = December)
  const day = currentDate.getUTCDate();

  // October 15 to December 7
  if (month === 9 && day >= 15) return true; // October 15-31
  if (month === 10) return true; // All of November
  if (month === 11 && day <= 7) return true; // December 1-7

  return false;
}

/**
 * Helper function to check if current date is in Medicare Advantage Open Enrollment Period (MA-OEP)
 * MA-OEP runs from January 1 to March 31 every year
 */
export function isInMedicareAdvantageOEP(
  currentDate: Date = new Date()
): boolean {
  // Use UTC components to avoid timezone issues with ISO date strings
  const month = currentDate.getUTCMonth(); // 0-indexed

  // January (0), February (1), March (2)
  return month >= 0 && month <= 2;
}
